import React, {
	useCallback,
	useMemo,
	cloneElement,
	useState,
	type KeyboardEvent,
	useEffect,
} from 'react';
import {
	useClipboard,
	type ListIssueFieldTransformedUnionForCopyPaste,
	type ClipboardItem,
} from '@atlassian/jira-business-fields/clipboard';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useCellActions } from '../cell-navigation';
import type { ClipboardControllerProps } from './types';
import { canBeReset, useClipboardErrorReporting } from './utils';

export const ClipboardController = ({
	children,
	issue,
	field,
	fieldId,
	isSelected,
	isEditMode,
	onKeyDown,
}: ClipboardControllerProps) => {
	const issueKey = issue.key;
	const transformedField = issue.fields?.find((f) => {
		const fieldId_ = field.id === 'key' ? 'issuekey' : field.id;
		return f.fieldId === fieldId_;
	});
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [clipboard, setClipboard] = useState<ClipboardItem | null>(null);
	const [isCutting, setIsCutting] = useState<boolean>(false);
	const [isCopying, setIsCopying] = useState<boolean>(false);

	const { setCopiedFromCell, clearCopiedFromCell } = useCellActions();

	const isEditable = field.fieldConfig?.isEditable ?? true;
	const canBeUnset = useMemo(() => canBeReset(field), [field]);

	const {
		copyCell,
		renderCopyHandlerComponent,
		renderPasteHandlerComponent,
		renderCutHandlerComponent,
	} = useClipboard(
		{ key: issueKey, issueId: issue.issueId, issueType: issue.issuetype },
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		transformedField as ListIssueFieldTransformedUnionForCopyPaste | undefined,
		fieldId,
	);

	const { reportError, dismissFlag } = useClipboardErrorReporting(field);

	const onPaste = useCallback(
		(success: boolean, error?: Error) => {
			dismissFlag();
			setClipboard(null);

			if (success) {
				fireTrackAnalytics(createAnalyticsEvent({}), 'cell pasted', {
					fieldType: field.type,
				});
			} else {
				reportError({
					action: 'paste',
					error,
				});
			}
		},
		[createAnalyticsEvent, dismissFlag, field.type, reportError],
	);

	const onCut = useCallback(
		(success: boolean, error?: Error) => {
			dismissFlag();
			setIsCutting(false);

			if (!success) {
				reportError({
					action: 'cut',
					error,
				});
			}
		},
		[dismissFlag, reportError],
	);

	const paste = useCallback(
		(event: ClipboardEvent) => {
			if (isEditMode) {
				return;
			}

			const data = event.clipboardData;

			setClipboard({
				plainText: data?.getData('text/plain'),
				html: data?.getData('text/html'),
			});
		},
		[setClipboard, isEditMode],
	);

	const copyFallback = useCallback(async () => {
		dismissFlag();
		try {
			setCopiedFromCell(field.id, issue.issueId);
			await copyCell();

			fireTrackAnalytics(createAnalyticsEvent({}), 'cell copied', {
				fieldType: field.type,
				hasCopied: true,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			reportError({
				action: 'copy',
				error,
			});
			clearCopiedFromCell();
		}
	}, [
		clearCopiedFromCell,
		copyCell,
		createAnalyticsEvent,
		dismissFlag,
		field.id,
		field.type,
		issue.issueId,
		reportError,
		setCopiedFromCell,
	]);

	const onCopyWithComponent = useCallback(
		async (success: boolean) => {
			setIsCopying(false);

			if (!success) {
				clearCopiedFromCell();
				return copyFallback();
			}

			fireTrackAnalytics(createAnalyticsEvent({}), 'cell copied', {
				fieldType: field.type,
				hasCopied: true,
			});
		},
		[createAnalyticsEvent, field.type, clearCopiedFromCell, copyFallback],
	);

	const copyWithComponent = useCallback(async () => {
		dismissFlag();
		setCopiedFromCell(field.id, issue?.issueId);
		setIsCopying(true);
	}, [dismissFlag, field.id, issue?.issueId, setCopiedFromCell]);

	const cut = useCallback(async () => {
		dismissFlag();
		try {
			setCopiedFromCell(field.id, issue.issueId);
			await copyCell();

			setIsCutting(true);

			fireTrackAnalytics(createAnalyticsEvent({}), 'cell cut', {
				fieldType: field.type,
				canBeReset: canBeUnset,
				hasCopied: true,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			reportError({
				action: 'cut',
				error,
			});
			clearCopiedFromCell();
		}
	}, [
		dismissFlag,
		setCopiedFromCell,
		field.id,
		field.type,
		issue.issueId,
		copyCell,
		canBeUnset,
		createAnalyticsEvent,
		reportError,
		clearCopiedFromCell,
	]);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLElement>) => {
			if (isSelected && !isEditMode && event.metaKey && event.key === 'c') {
				copyWithComponent();
				return;
			}

			if (isSelected && !isEditMode && event.metaKey && event.key === 'x') {
				cut();
				return;
			}

			onKeyDown(event);
		},
		[isSelected, isEditMode, onKeyDown, copyWithComponent, cut],
	);

	useEffect(() => {
		if (isSelected) {
			document.addEventListener('paste', paste, true);
		} else {
			document.removeEventListener('paste', paste, true);
		}

		return () => {
			document.removeEventListener('paste', paste, true);
		};
	}, [isSelected, paste]);

	return (
		<>
			{cloneElement(children, {
				onKeyDown: handleKeyDown,
			})}
			{clipboard &&
				renderPasteHandlerComponent({
					clipboard,
					onPaste,
					isEditable,
					onBeforePaste: clearCopiedFromCell,
				})}
			{isCutting && renderCutHandlerComponent({ onCut, isEditable: canBeUnset })}
			{isCopying && renderCopyHandlerComponent({ onCopy: onCopyWithComponent })}
		</>
	);
};
